import type { DownloadJobType } from "@volgo/core/contract";
import { DOWNLOAD_STATUS_LABELS, STAGE_LABELS } from "../ui/labels";

const MiB = 1024 * 1024;
const mib = (bytes: number) =>
  (bytes / MiB).toLocaleString("ko-KR", { maximumFractionDigits: bytes < 10 * MiB ? 1 : 0 });
function remaining(seconds: number) {
  if (seconds < 60) return "1분 미만";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `약 ${minutes}분`;
  return minutes % 60 ? `약 ${Math.floor(minutes / 60)}시간 ${minutes % 60}분` : `약 ${minutes / 60}시간`;
}

type Job = Pick<DownloadJobType, "route" | "stage" | "status" | "progress"> &
  Partial<Pick<DownloadJobType, "transfer">>;

/** All download surfaces describe the current stage, not the whole job's percentage. */
export function downloadProgress(job: Job) {
  const receivingArchive = job.route === "archive" && job.stage === "fetch";
  const label =
    job.status !== "running" || !job.stage
      ? DOWNLOAD_STATUS_LABELS[job.status]
      : receivingArchive
        ? "다운로드·압축 해제"
        : STAGE_LABELS[job.stage];
  const bytes = receivingArchive && job.status === "running" ? job.transfer : null;
  if (bytes) {
    // While the ZIP arrives, received bytes are the honest measure; file counts lag big files.
    const total = bytes.totalBytes && bytes.totalBytes > 0 ? bytes.totalBytes : null;
    const parts = [
      total ? `${mib(bytes.doneBytes)}/${mib(total)} MiB` : `${mib(bytes.doneBytes)} MiB`,
      bytes.bytesPerSecond > 0 ? `${mib(bytes.bytesPerSecond)} MiB/s` : null,
      total && bytes.bytesPerSecond > 0
        ? `${remaining(Math.max(0, total - bytes.doneBytes) / bytes.bytesPerSecond)} 남음`
        : null,
      `압축 해제 ${job.progress.done}개`,
    ].filter(Boolean);
    const count = parts.join(" · ");
    return {
      label,
      count,
      done: total ? Math.min(bytes.doneBytes, total) : bytes.doneBytes,
      total,
      text: [label, count].join(" · "),
    };
  }
  const total = job.progress.total && job.progress.total > 0 ? job.progress.total : null;
  const done = total ? Math.min(job.progress.done, total) : job.progress.done;
  const count = total
    ? `${done}/${total}개`
    : receivingArchive
      ? `압축 해제 ${done}개`
      : done > 0
        ? `${done}개`
        : "";
  return { label, count, done, total, text: [label, count].filter(Boolean).join(" · ") };
}
